import {
  ArrowRightOutlined,
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DownOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Button, Popover, Select, Skeleton, Tooltip } from "antd";
import dayjs from "dayjs";
import React from "react";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import type { ScheduleSlot } from "../../service/schedulePick.service";
import { resolveClinicZone, scheduleSlotLabel, scheduleSlotRangeLabel } from "../../utils/clientSchedule";
import { type QuickStartView, firstNameOf, slotTimingLabel } from "./quickStart";
import styles from "./quickstart.module.scss";
import type { QuickStartSchedule, SessionTemplateState } from "./useQuickStart";

// The chip and "who is next" are relative to now; re-render on a slow tick.
const useNow = (intervalMs = 30_000) => {
  const [now, setNow] = React.useState(() => dayjs());
  React.useEffect(() => {
    const id = setInterval(() => setNow(dayjs()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
};

const useClinicZone = (schedule: QuickStartSchedule) =>
  React.useMemo(() => resolveClinicZone(schedule.pick?.clinic_tz), [schedule.pick?.clinic_tz]);

/** A colleague's patient names the provider (an MA's day); your own doesn't. */
const providerOf = (slot: ScheduleSlot) => (slot.client.is_owned === false ? slot.client.owner_name : null);

/** See all's row: the start time is enough to find someone in a list. */
const slotMeta = (slot: ScheduleSlot, zone: string) =>
  [scheduleSlotLabel(slot.scheduled_at, zone), providerOf(slot)].filter(Boolean).join(" • ");

/** On the card itself: the whole slot, "3:30 PM – 4:00 PM". */
const slotRangeMeta = (slot: ScheduleSlot, zone: string) =>
  [scheduleSlotRangeLabel(slot.scheduled_at, slot.ends_at, zone), providerOf(slot)].filter(Boolean).join(" • ");

/** "Template: Family Medicine SOAP ▾" — the template every Quick Start on the card will use. */
export const TemplateLine = ({ session }: { session: SessionTemplateState }) => {
  const { template } = session;
  return (
    <div className={styles.Appointment__meta}>
      <span>Template:</span>
      {session.ready ? (
        <Tooltip
          title={
            template ? null : "No template on file yet. Start now and choose one when you submit, or pick it here."
          }
        >
          <Select
            size="small"
            variant="borderless"
            showSearch
            data-testid="quick-start-template"
            className={`${styles.Appointment__template} ${template ? "" : styles["Appointment__template--pending"]}`}
            popupMatchSelectWidth={260}
            value={template?.value}
            placeholder="Choose"
            options={session.groups}
            optionFilterProp="searchText"
            labelRender={() => template?.name ?? "Choose"}
            onOpenChange={(open) => open && session.loadGallery()}
            onChange={(value) => {
              const option = session.options.find((candidate) => candidate.value === value);
              if (option) session.choose(option);
            }}
          />
        </Tooltip>
      ) : (
        <Skeleton.Input active size="small" className={styles.Appointment__templateLoading} />
      )}
    </div>
  );
};

/** "See all ▾": today's appointments to switch to, and a way to record without a patient. */
export const SeeAll = ({
  schedule,
  currentId,
  onRecordWithoutPatient,
}: {
  schedule: QuickStartSchedule;
  currentId: number | null;
  onRecordWithoutPatient: () => void;
}) => {
  const { referIndividualBy } = useSpecialityReference();
  const zone = useClinicZone(schedule);
  const [open, setOpen] = React.useState(false);
  const documented = schedule.pick?.documented ?? [];
  const list = (
    <div className={styles.Appointments} data-testid="quick-start-appointments">
      <div className={styles.Appointments__header}>Your appointments. Select to switch</div>
      <div className={styles.Appointments__list}>
        {schedule.upcoming.map((slot) => {
          const selected = slot.client.id === currentId;
          return (
            <button
              type="button"
              key={slot.client.id}
              className={`${styles.Appointments__row} ${selected ? styles["Appointments__row--selected"] : ""}`}
              onClick={() => {
                if (slot.client.id != null) schedule.choose(slot.client.id);
                setOpen(false);
              }}
            >
              <span className={styles.Appointments__rowText}>
                <span className={styles.Appointments__name}>{slot.client.name}</span>
                <span className={styles.Appointments__meta}>{slotMeta(slot, zone)}</span>
              </span>
              {selected && <CheckOutlined className={styles.Appointments__check} />}
            </button>
          );
        })}
        {documented.length > 0 && (
          <>
            <div className={styles.Appointments__group}>Documented today</div>
            {documented.map((slot) => (
              <div key={slot.client.id} className={`${styles.Appointments__row} ${styles["Appointments__row--done"]}`}>
                <span className={styles.Appointments__rowText}>
                  <span className={styles.Appointments__name}>{slot.client.name}</span>
                  <span className={styles.Appointments__meta}>{slotMeta(slot, zone)}</span>
                </span>
                <CheckOutlined className={styles.Appointments__done} />
              </div>
            ))}
          </>
        )}
      </div>
      <button
        type="button"
        className={styles.Appointments__footer}
        onClick={() => {
          setOpen(false);
          onRecordWithoutPatient();
        }}
      >
        Record without a {referIndividualBy.toLowerCase()} <ArrowRightOutlined />
      </button>
    </div>
  );
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      styles={{ body: { padding: 0 } }}
      content={list}
    >
      <button type="button" className={styles.Appointment__seeAll} data-testid="quick-start-see-all">
        <CalendarOutlined /> See all <DownOutlined className={styles.Appointment__caret} />
      </button>
    </Popover>
  );
};

type QuickStartCardProps = {
  schedule: QuickStartSchedule;
  session: SessionTemplateState;
  /** The patient whose Quick Start is starting, while it starts. */
  startingId: number | null;
  onQuickStart: (slot: ScheduleSlot) => void;
  onRecordWithoutPatient: () => void;
  /** × : the schedule's dismiss, wrapped by the caller so it can move focus. */
  onDismiss: () => void;
  dismissRef?: React.Ref<HTMLButtonElement>;
  footer: React.ReactNode;
};

/** The ×'s tooltip and accessible name. It reads "not this patient", never "turn a feature off". */
const dismissCopy = (view: QuickStartView, term: string): { tip: string; aria: string } => {
  if (view.kind === "choose") {
    return { tip: `None of these? Dismiss and use Start ${term}`, aria: "Dismiss these suggestions" };
  }
  if (view.kind !== "quick") return { tip: "", aria: "" };
  const name = view.slot.client.name || "";
  if (view.chosen) return { tip: "Clear this pick", aria: `Clear this pick, ${name}` };
  const first = firstNameOf(name);
  return {
    tip: `${first ? `Not ${first}? ` : ""}Dismiss the suggestion and use Start ${term}`,
    aria: `Dismiss suggestion for ${name}`,
  };
};

/**
 * Quick Start: the patient (or the few patients booked around now) and the template are
 * decided and on screen before anything records; one click starts. × (at the end of the
 * patient's row, so it reads "not this patient") turns the pick down and the card becomes
 * Start Encounter.
 */
const QuickStartCard = ({
  schedule,
  session,
  startingId,
  onQuickStart,
  onRecordWithoutPatient,
  onDismiss,
  dismissRef,
  footer,
}: QuickStartCardProps) => {
  const now = useNow();
  const zone = useClinicZone(schedule);
  const { sessionTerm } = useSpecialityReference();
  const { view } = schedule;
  if (view.kind !== "quick" && view.kind !== "choose") return null;
  const lead = view.kind === "quick" ? view.slot : view.slots[0];
  const busy = startingId !== null || !session.ready;
  const { tip, aria } = dismissCopy(view, sessionTerm);
  const dismiss = (
    <Tooltip title={tip} trigger={["hover", "focus"]}>
      <button
        type="button"
        ref={dismissRef}
        aria-label={aria}
        className={styles.Appointment__dismiss}
        disabled={startingId !== null}
        onClick={onDismiss}
        data-testid="quick-start-dismiss"
      >
        <CloseOutlined />
      </button>
    </Tooltip>
  );

  return (
    <div className={styles.Card} data-testid="quick-start-card">
      <div className={styles.Appointment}>
        <div className={styles.Appointment__top}>
          <span className={styles.Appointment__chip} data-testid="quick-start-timing">
            <ClockCircleOutlined />{" "}
            {view.kind === "quick"
              ? slotTimingLabel(lead.scheduled_at, now)
              : `${view.slots.length + view.more} booked around now`}
          </span>
          <SeeAll
            schedule={schedule}
            currentId={view.kind === "quick" ? lead.client.id ?? null : null}
            onRecordWithoutPatient={onRecordWithoutPatient}
          />
        </div>

        {view.kind === "quick" ? (
          <>
            <div className={styles.Appointment__subject}>
              <div className={styles.Appointment__name} data-testid="quick-start-patient" title={lead.client.name}>
                {lead.client.name}
              </div>
              {dismiss}
            </div>
            <TemplateLine session={session} />
            <div className={styles.Appointment__meta} data-testid="quick-start-slot">
              {scheduleSlotRangeLabel(lead.scheduled_at, lead.ends_at, zone)}
              {providerOf(lead) && <span className={styles.Appointment__provider}>• {providerOf(lead)}</span>}
            </div>
          </>
        ) : (
          <>
            <div className={styles.Appointment__subject}>
              <div className={styles.Appointment__question}>Who's in the room?</div>
              {dismiss}
            </div>
            <div className={styles.Candidates} data-testid="quick-start-candidates">
              {view.slots.map((slot) => (
                <button
                  type="button"
                  key={slot.client.id}
                  className={styles.Candidates__row}
                  disabled={busy}
                  onClick={() => onQuickStart(slot)}
                  data-testid="quick-start-candidate"
                >
                  <span className={styles.Candidates__text}>
                    <span className={styles.Candidates__name}>{slot.client.name}</span>
                    <span className={styles.Candidates__meta}>{slotRangeMeta(slot, zone)}</span>
                  </span>
                  <span className={styles.Candidates__go}>
                    <ThunderboltOutlined /> {startingId === slot.client.id ? "Starting…" : "Start"}
                  </span>
                </button>
              ))}
            </div>
            {view.more > 0 && <div className={styles.Candidates__more}>+{view.more} more in See all</div>}
            <TemplateLine session={session} />
          </>
        )}
      </div>

      {view.kind === "quick" && (
        <Button
          type="primary"
          block
          icon={<ThunderboltOutlined />}
          loading={busy}
          className={styles.Card__primary}
          onClick={() => onQuickStart(lead)}
          data-testid="quick-start-start"
        >
          {[`Start ${sessionTerm}`, firstNameOf(lead.client.name)].filter(Boolean).join(" for ")}
        </Button>
      )}
      {footer}
    </div>
  );
};

export default QuickStartCard;
